import { isEmpty, isNil } from 'lodash';
import { ComponentPropsWithoutRef, useContext, useMemo } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { DropTarget } from '@shared/components/DropTarget';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { FileUpload } from '@shared/components/blocks/types';
import { Styles as ButtonStyles } from '@shared/components/button';
import { MBFileDropListener } from '@shared/components/form/fileDropListener';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBTouchableOverlay } from '@shared/components/touchable';
import { MBTextBody1Bold } from '@shared/components/typography';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { ColorThemeContext, grid } from '@shared/styles';
import { MBBlockLabel } from '../../utils/label';
import { InputBlockMedia } from './InputBlockMedia';
import { FileUploadHookInput, useFileUpload } from './hooks';

interface Props extends FileUploadHookInput {
  label?: string;
  placeholder?: string;
  icon?: MBIconV2Name;
  files: FileUpload[];
  error?: boolean;
  mode?: 'lean' | 'full';
  isReadOnly?: boolean;
}

// displays file/image input with list of files
export function FileInput({
  files,
  label,
  placeholder,
  icon,
  error,
  acceptedContentTypes,
  onFileAdded,
  onFileRemoved,
  mode = 'full',
  isReadOnly = false,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const responsiveForms = useResponsiveFormContext();
  const { handleDrop, showFilePicker, uploading } = useFileUpload({
    onFileAdded,
    onFileRemoved,
    acceptedContentTypes,
  });

  const dropTargetStyle = mode === 'full' ? styles.fullDropTarget : styles.leanDropTarget;

  const containerProps = useMemo<ComponentPropsWithoutRef<typeof MBView>>(() => {
    if (mode === 'full') {
      return {
        fullWidth: true,
        paddingVertical: 1,
        paddingHorizontal: 1,
      };
    } else {
      return {
        style: styles.leanContainer,
        paddingHorizontal: 1,
      };
    }
  }, [mode]);

  const previewStyle = useMemo<ComponentPropsWithoutRef<typeof InputBlockMedia>['style']>(
    () => (mode === 'full' ? styles.fullPreview : styles.leanPreview),
    [mode],
  );

  const displayInitiator = !isReadOnly && (!files.length || mode === 'lean');

  const activeDropTarget = (
    <DropTarget
      style={dropTargetStyle}
      acceptedContentTypes={acceptedContentTypes}
      placeholder={placeholder ?? ''}
      mode={mode}
      state="active"
    />
  );

  return (
    <MBView {...containerProps}>
      {!isNil(label) && (
        <View paddingLeft={3} paddingVertical={1}>
          <MBBlockLabel label={label} />
        </View>
      )}
      <MBFileDropListener
        onFilesDropped={handleDrop}
        fill
        overlay={activeDropTarget}
        disabled={isReadOnly}
      >
        {!isEmpty(files) && (
          <View>
            {!isReadOnly && !responsiveForms && (
              <TouchableOpacity
                onPress={showFilePicker}
                disabled={uploading}
                style={[
                  ButtonStyles.button,
                  ButtonStyles.buttonSizeNormal,
                  ButtonStyles.wrap,
                  styles.button,
                  {
                    borderColor: colors.input.borderInactive,
                    backgroundColor: colors.button.tertiary,
                  },
                ]}
              >
                <MBIconV2
                  name={icon}
                  size={24}
                  color={colors.foregroundActive}
                  style={[styles.buttonIcon]}
                />
                <MBTextBody1Bold style={[styles.buttonText, { color: colors.foregroundActive }]}>
                  {placeholder}
                </MBTextBody1Bold>
              </TouchableOpacity>
            )}
            <InputBlockMedia
              files={files}
              onRemoved={onFileRemoved}
              editable={!isReadOnly}
              style={previewStyle}
              compactFileView={false}
              horizontalPadding={responsiveForms ? 0 : 0.5}
              verticalPadding={responsiveForms ? 0 : 1.5}
            ></InputBlockMedia>
            {!isReadOnly && responsiveForms && (
              <TouchableOpacity
                onPress={showFilePicker}
                disabled={uploading}
                style={[
                  ButtonStyles.button,
                  ButtonStyles.buttonSizeNormal,
                  ButtonStyles.wrap,
                  styles.button,
                  {
                    borderColor: colors.input.borderInactive,
                    backgroundColor: colors.button.tertiary,
                    width: 'auto',
                    paddingLeft: grid(1.5),
                    paddingRight: grid(2.5),
                    marginTop: grid(1),
                    alignSelf: 'center',
                  },
                ]}
              >
                <MBIconV2
                  name={icon}
                  size={24}
                  color={colors.foregroundActive}
                  style={[styles.buttonIcon]}
                />
                <MBTextBody1Bold style={[styles.buttonText, { color: colors.foregroundActive }]}>
                  {placeholder}
                </MBTextBody1Bold>
              </TouchableOpacity>
            )}
          </View>
        )}
        {displayInitiator && (
          <MBTouchableOverlay onPress={showFilePicker} fill overlay={activeDropTarget}>
            <DropTarget
              loading={uploading}
              style={dropTargetStyle}
              placeholder={placeholder ?? ''}
              state={error ? 'error' : 'inactive'}
              mode={mode}
              acceptedContentTypes={acceptedContentTypes}
            />
          </MBTouchableOverlay>
        )}
      </MBFileDropListener>
    </MBView>
  );
}

const styles = StyleSheet.create({
  leanContainer: {
    maxWidth: '60%',
    paddingBottom: grid(1),
  },
  leanDropTarget: {
    flexDirection: 'row',
  },
  fullDropTarget: {
    borderRadius: 8,
    borderStyle: 'dashed',
    borderWidth: 2,
  },
  fullPreview: { paddingHorizontal: grid(3) },
  leanPreview: {
    maxWidth: '60%',
    minHeight: 100,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 'auto',
    minHeight: 0,
    paddingVertical: grid(1),
    paddingHorizontal: grid(1),
    width: '100%',
    justifyContent: 'center',
  },
  buttonIcon: {
    marginRight: grid(1),
    flexShrink: 0,
  },
  buttonText: {
    flexShrink: 1,
  },
});
